<template>
    <div class="RiskProfileHeader">
        <div class="RiskProfileHeader__titleRow">
            <NewButton
                variant="flat"
                color="neutral"
                size="icon"
                class="RiskProfileHeader__backButton"
                aria-label="Back"
                @click="onCloseButtonClick"
            >
                <FontAwesomeIcon :icon="faAngleLeft" />
            </NewButton>
            <div class="RiskProfileHeader__nameSection">
                <div
                    v-if="viewMode && !isInvalid"
                    class="RiskProfileHeader__riskProfileNameText group"
                    :class="{'RiskProfileHeader__riskProfileNamePlaceholder': !riskProfileName}"
                    @click="displayInput"
                >
                    <div class="RiskProfileHeader__nameContent">
                        <span class="RiskProfileHeader__nameTitle">
                            {{ riskProfileName || 'Enter Risk Profile Name' }}
                        </span>
                        <div class="RiskProfileHeader__editHint">
                            <span class="RiskProfileHeader__editText">Click to edit</span>
                            <EidIcon
                                :icon-src="faPenToSquare"
                                class="RiskProfileHeader__editIcon"
                            />
                        </div>
                    </div>
                </div>
                <FormInput v-else class="RiskProfileHeader__formInput">
                    <input
                        ref="input"
                        class="RiskProfileHeader__input"
                        :class="{'RiskProfileHeader__input--invalid': isInvalid}"
                        :value="riskProfileName"
                        placeholder="Enter Risk Profile Name"
                        @blur="displayText"
                        @input="onNameInput"
                    >
                </FormInput>
                <span class="RiskProfileHeader__country">Country: {{ countryCode }}</span>
            </div>
        </div>
        <div class="RiskProfileHeader__action">
            <div class="RiskProfileHeader__headerSaveButtonWrapper">
                <RiskProfileSaveButton
                    :disabled="!riskProfileName"
                    @save-active="onSave(true)"
                    @save-discover="onSave(false)"
                />
            </div>
            <div class="RiskProfileHeader__headerStatus">
                <span>Status: </span>
                <span
                    class="RiskProfileHeader__headerStatusText"
                    :class="{'RiskProfileHeader__headerStatusText--active': riskProfileActive}"
                    data-test-id="RiskProfileHeader__saveStatus"
                >
                    {{ status }}
                </span>
                <div
                    v-if="dirtyState"
                    class="RiskProfileHeader__saveStatus"
                    data-test-id="RiskProfileHeader__saveStatus"
                >
                    Unsaved Changes
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    $eidSecureBlue-10: #eaecee;
    $border-color: $eidSecureBlue-10;
    $eidBgGrayTonesLightest: #f8fafc;
    $eidWhite: #ffffff;
    $eidDangerRed: #f15b47;
    $eidDangerRed-25: #fbd6d1;

    .RiskProfileHeader {
        display: flex;
        padding: 21px;
        align-items: center;
        background-color: $eidWhite;
        min-height: 70px;

        &__titleRow {
            display: flex;
            align-items: flex-start;
            gap: 0.25rem; // gap-1
            flex: 1;
        }

        &__backButton {
            flex-shrink: 0;
        }

        &__nameSection {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
        }

        &__formInput {
            flex: 1;
            width: 100%;
        }

        &__input {
            height: 40px;
            border: none;
            text-overflow: ellipsis;
            border-radius: 8px;
            font-size: 17.5px;
            padding: 0 15px;
            font-weight: bold;
            min-width: 100%;
            outline: none;
            background: transparent;

            &:focus:not([disabled]) {
                border: 1px solid $border-color;
                border-radius: 10px;
                background: $eidWhite;
            }

            &--invalid {
                border: 1px solid $eidDangerRed;
                background-color: $eidWhite;

                &:focus:not([disabled]) {
                    border: 1px solid $eidDangerRed;
                }
            }
        }

        &__riskProfileNameText {
            width: 100%;
            text-align: left;
            padding: 7px 14px;
            border-radius: 2px;
            border: 1px solid $border-color;
            background-color: $eidBgGrayTonesLightest;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            transition: all 0.2s ease-in-out;
            cursor: pointer;
            min-height: 40px;
            display: flex;
            align-items: center;

            &:hover {
                background-color: $eidBgGrayTonesLightest;
                border-color: $eidSecureBlue-10;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
        }

        &__nameContent {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        }

        &__nameTitle {
            font-size: 17.5px;
            font-weight: 500;
            @apply text-eidQualityBlack;
            transition: color 0.2s ease-in-out;

            .group:hover & {
                @apply text-eidQualityBlack-75;
            }
        }

        &__editHint {
            display: flex;
            align-items: center;
            gap: 8px;
            transition: opacity 0.2s ease-in-out;
            opacity: 1;
        }

        &__editText {
            font-size: 0.75rem;
            @apply text-eidLightGray;
            font-weight: normal;
        }

        &__editIcon {
            width: 16px;
            height: 16px;
            @apply text-eidLightGray;
            transition: color 0.2s ease-in-out;

            .group:hover & {
                @apply text-eidTrustGreen;
            }
        }

        &__riskProfileNamePlaceholder {
            .RiskProfileHeader__nameTitle {
                @apply text-eidLightGray;
            }
        }

        &__country {
            @apply text-eidLightGray;

            margin-left: 15px;
            margin-top: 2px;
            font-size: 0.9rem;
        }

        &__action {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-left: auto;
        }

        &__headerStatus {
            font-size: 0.75em;
        }

        &__headerStatus,
        &__headerSaveButtonWrapper {
            @apply text-eidLightGray;

            padding: 0 15px;
        }

        &__headerStatusText--active {
            @apply text-eidTrustGreen;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleLeft, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormInput } from '@evidentid/dashboard-commons/components/Form';

    import NewButton from '@/components/new-button/NewButton.vue';
    import RiskProfileSaveButton
        from '@/modules/decisioning-criteria/components/RiskProfileSaveButton/RiskProfileSaveButton.vue';

    export default defineComponent({
        name: 'RiskProfileHeader',
        components: {
            EidIcon,
            FontAwesomeIcon,
            FormInput,
            NewButton,
            RiskProfileSaveButton,
        },
        props: {
            riskProfileName: {
                type: String as PropType<string>,
                required: true,
            },
            countryCode: {
                type: [ String, null ] as PropType<string | null>,
                default: '',
            },
            riskProfileActive: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            dirtyState: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'back', 'input', 'save' ],
        data() {
            return {
                faAngleLeft,
                faPenToSquare,
                isDirty: false,
                viewMode: true,
            };
        },
        computed: {
            status(): string {
                return this.riskProfileActive ? 'Active' : 'Draft';
            },
            isInvalid(): boolean {
                return this.isDirty && !this.riskProfileName;
            },
        },
        methods: {
            onCloseButtonClick(): void {
                this.$emit('back');
            },
            onNameInput(event: InputEvent): void {
                this.isDirty = true;
                this.$emit('input', (event.target as HTMLInputElement).value);
            },
            async displayInput(): Promise<void> {
                this.viewMode = false;
                await this.$nextTick();
                (this.$refs.input as HTMLInputElement).focus();
            },
            displayText(): void {
                this.viewMode = true;
            },
            onSave(isActive: boolean): void {
                this.$emit('save', { isActive });
            },
        },
    });
</script>
